import React from 'react';
import { X, ExternalLink, Github, Cloud, Zap, Globe, Shield } from 'lucide-react';

const Introduction = ({ onClose }) => {
  return (
    <div className="introduction-overlay">
      <div className="introduction-content">
        <div className="introduction-header">
          <h1>🌤️ Weather App</h1>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="introduction-body">
          <section className="intro-section">
            <h2>🚀 Try the App</h2>
            <p>
              Search for weather in any city worldwide! Get real-time weather data, 
              5-day forecasts, and beautiful weather visualizations.
            </p>
            <div className="demo-features">
              <div className="demo-item">
                <span className="demo-icon">🔍</span>
                <span>Search any city</span>
              </div>
              <div className="demo-item">
                <span className="demo-icon">🌡️</span>
                <span>Real-time temperature</span>
              </div>
              <div className="demo-item">
                <span className="demo-icon">📅</span>
                <span>5-day forecast</span>
              </div>
              <div className="demo-item">
                <span className="demo-icon">📱</span>
                <span>Responsive design</span>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>📋 About This Project</h2>
            <p>
              A modern, full-stack weather application built with React frontend and Python backend, 
              deployed on Cloudflare's global network. This project demonstrates modern web development 
              practices, API integration, and cloud deployment.
            </p>
            <div className="project-highlights">
              <div className="highlight-item">
                <Cloud className="highlight-icon" />
                <div>
                  <h4>Cloudflare Workers</h4>
                  <p>Python backend running on global edge network</p>
                </div>
              </div>
              <div className="highlight-item">
                <Zap className="highlight-icon" />
                <div>
                  <h4>Real-time Data</h4>
                  <p>Live weather data from OpenWeatherMap API</p>
                </div>
              </div>
              <div className="highlight-item">
                <Globe className="highlight-icon" />
                <div>
                  <h4>Global Access</h4>
                  <p>Available worldwide with sub-second load times</p>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🏗️ Project Structure</h2>
            <div className="structure-diagram">
              <div className="structure-item">
                <div className="structure-title">Frontend (React)</div>
                <div className="structure-details">
                  <div>• Modern React 18 with hooks</div>
                  <div>• Responsive CSS with glassmorphism</div>
                  <div>• Axios for API communication</div>
                  <div>• Lucide React for icons</div>
                </div>
              </div>
              <div className="structure-arrow">↕️</div>
              <div className="structure-item">
                <div className="structure-title">Backend (Python)</div>
                <div className="structure-details">
                  <div>• Python on Cloudflare Workers</div>
                  <div>• RESTful API design</div>
                  <div>• OpenWeatherMap integration</div>
                  <div>• CORS and error handling</div>
                </div>
              </div>
              <div className="structure-arrow">↕️</div>
              <div className="structure-item">
                <div className="structure-title">External API</div>
                <div className="structure-details">
                  <div>• OpenWeatherMap weather data</div>
                  <div>• Real-time weather conditions</div>
                  <div>• 5-day weather forecasts</div>
                  <div>• Global city support</div>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>💻 Code Explanation</h2>
            <div className="code-explanation">
              <div className="code-section">
                <h4>Frontend Architecture</h4>
                <ul>
                  <li><strong>App.js</strong>: Main component with state management</li>
                  <li><strong>WeatherCard</strong>: Displays current weather data</li>
                  <li><strong>Forecast</strong>: Shows 5-day weather predictions</li>
                  <li><strong>SearchBar</strong>: City search functionality</li>
                  <li><strong>weatherService.js</strong>: API communication layer</li>
                </ul>
              </div>
              <div className="code-section">
                <h4>Backend Architecture</h4>
                <ul>
                  <li><strong>WeatherAPI Class</strong>: Main API handler</li>
                  <li><strong>Route Handling</strong>: URL parsing and routing</li>
                  <li><strong>OpenWeatherMap Integration</strong>: External API calls</li>
                  <li><strong>Data Formatting</strong>: Response transformation</li>
                  <li><strong>Error Handling</strong>: Comprehensive error management</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🛠️ Tech Stack</h2>
            <div className="tech-stack">
              <div className="tech-category">
                <h4>Frontend</h4>
                <div className="tech-items">
                  <span className="tech-item">React 18</span>
                  <span className="tech-item">JavaScript ES6+</span>
                  <span className="tech-item">CSS3</span>
                  <span className="tech-item">Axios</span>
                  <span className="tech-item">Lucide React</span>
                </div>
              </div>
              <div className="tech-category">
                <h4>Backend</h4>
                <div className="tech-items">
                  <span className="tech-item">Python 3</span>
                  <span className="tech-item">Cloudflare Workers</span>
                  <span className="tech-item">RESTful API</span>
                  <span className="tech-item">JSON</span>
                  <span className="tech-item">HTTP/HTTPS</span>
                </div>
              </div>
              <div className="tech-category">
                <h4>Deployment</h4>
                <div className="tech-items">
                  <span className="tech-item">Cloudflare Pages</span>
                  <span className="tech-item">Cloudflare Workers</span>
                  <span className="tech-item">Global CDN</span>
                  <span className="tech-item">SSL/TLS</span>
                  <span className="tech-item">Wrangler CLI</span>
                </div>
              </div>
              <div className="tech-category">
                <h4>External Services</h4>
                <div className="tech-items">
                  <span className="tech-item">OpenWeatherMap API</span>
                  <span className="tech-item">Weather Icons</span>
                  <span className="tech-item">Global Weather Data</span>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🎯 Key Learning Points</h2>
            <div className="learning-points">
              <div className="learning-item">
                <h4>🌐 Cloudflare Workers Python</h4>
                <p>Learned to deploy Python applications on Cloudflare's edge network, including URL parsing, CORS handling, and environment variable management.</p>
              </div>
              <div className="learning-item">
                <h4>⚛️ Modern React Development</h4>
                <p>Implemented React hooks, component composition, state management, and responsive design with modern CSS techniques.</p>
              </div>
              <div className="learning-item">
                <h4>🔌 API Integration</h4>
                <p>Integrated external APIs (OpenWeatherMap), handled async operations, error states, and data transformation.</p>
              </div>
              <div className="learning-item">
                <h4>🚀 Full-Stack Deployment</h4>
                <p>Deployed both frontend and backend to Cloudflare's platform, understanding global CDN, edge computing, and performance optimization.</p>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🚀 Deployment Guide</h2>
            <div className="deployment-guide">
              <div className="deployment-step">
                <h4>1. Backend Deployment (Python API)</h4>
                <div className="code-block">
                  <code>
                    # Set up Python API<br/>
                    cd weather-api-python<br/>
                    wrangler secret put OPENWEATHER_API_KEY<br/>
                    wrangler deploy
                  </code>
                </div>
              </div>
              <div className="deployment-step">
                <h4>2. Frontend Deployment (React App)</h4>
                <div className="code-block">
                  <code>
                    # Build and deploy React app<br/>
                    cd weather-app-frontend<br/>
                    npm run build<br/>
                    npx wrangler pages project create weather-app-frontend<br/>
                    npx wrangler pages deploy build
                  </code>
                </div>
              </div>
              <div className="deployment-step">
                <h4>3. Environment Configuration</h4>
                <div className="code-block">
                  <code>
                    # Set up API key<br/>
                    wrangler secret put OPENWEATHER_API_KEY<br/>
                    # Get your OpenWeatherMap API key from:<br/>
                    # https://openweathermap.org/api
                  </code>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>📝 Development Commands</h2>
            <div className="commands-section">
              <div className="command-group">
                <h4>Backend Development</h4>
                <div className="command-item">
                  <code>wrangler dev</code>
                  <span>Start local development server</span>
                </div>
                <div className="command-item">
                  <code>wrangler deploy</code>
                  <span>Deploy to production</span>
                </div>
                <div className="command-item">
                  <code>wrangler tail</code>
                  <span>View live logs</span>
                </div>
              </div>
              <div className="command-group">
                <h4>Frontend Development</h4>
                <div className="command-item">
                  <code>npm start</code>
                  <span>Start development server</span>
                </div>
                <div className="command-item">
                  <code>npm run build</code>
                  <span>Build for production</span>
                </div>
                <div className="command-item">
                  <code>npm test</code>
                  <span>Run tests</span>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🔗 Live URLs</h2>
            <div className="live-urls">
              <div className="url-item">
                <ExternalLink className="url-icon" />
                <div>
                  <h4>Weather App Frontend</h4>
                  <a href="https://b448a1f5.weather-app-frontend-9tu.pages.dev" target="_blank" rel="noopener noreferrer">
                    https://b448a1f5.weather-app-frontend-9tu.pages.dev
                  </a>
                </div>
              </div>
              <div className="url-item">
                <ExternalLink className="url-icon" />
                <div>
                  <h4>Weather API Backend</h4>
                  <a href="https://weather-api-python.oskarmansanqu.workers.dev" target="_blank" rel="noopener noreferrer">
                    https://weather-api-python.oskarmansanqu.workers.dev
                  </a>
                </div>
              </div>
              <div className="url-item">
                <ExternalLink className="url-icon" />
                <div>
                  <h4>API Health Check</h4>
                  <a href="https://weather-api-python.oskarmansanqu.workers.dev/health" target="_blank" rel="noopener noreferrer">
                    https://weather-api-python.oskarmansanqu.workers.dev/health
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="intro-section">
            <h2>🎯 Next Steps</h2>
            <div className="next-steps">
              <div className="step-item">
                <span className="step-number">1</span>
                <div>
                  <h4>Enhanced Features</h4>
                  <p>Add weather maps, alerts, historical data, and location-based services</p>
                </div>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <div>
                  <h4>Performance Optimization</h4>
                  <p>Implement caching, lazy loading, and progressive web app features</p>
                </div>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <div>
                  <h4>Testing & Monitoring</h4>
                  <p>Add comprehensive testing, error monitoring, and analytics</p>
                </div>
              </div>
              <div className="step-item">
                <span className="step-number">4</span>
                <div>
                  <h4>Mobile App</h4>
                  <p>Develop native mobile applications using React Native</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="introduction-footer">
          <p>Built with ❤️ using React & Python on Cloudflare</p>
          <div className="footer-icons">
            <Shield className="footer-icon" />
            <span>Secure</span>
            <Globe className="footer-icon" />
            <span>Global</span>
            <Zap className="footer-icon" />
            <span>Fast</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Introduction;
